import { Component, OnDestroy, OnInit, AfterViewInit, ChangeDetectionStrategy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { LocationModel } from "src/app/models/location.model";
import { CommonService } from "src/app/services/common.service";
import { ToastService } from "src/app/services/toast.service";
import nameOf from "src/app/shared/form/nameOf.helper";
import { detailExpandAnimation } from "src/app/animations/detailExpand.animation";
import { BaseGridComponent } from "src/app/shared/grid/baseGrid.component";
import { LocationGroupService } from "src/app/services/locationGroup.service";
import { LocationService } from "src/app/services/location.service";
import { LocationGroup } from "src/app/models/locationGroup.model";
import {
    ILocationDialogData,
    LocationDialogComponent,
} from "src/app/modals/location-dialog/location-dialog.component";
import { DeleteDialog } from "src/app/shared/delete-dialog/delete-dialog.component";
import { IFormDialogData } from "src/app/shared/form/form.component";
import { LocationGroupDialog } from "src/app/modals/location-group-dialog/location-group-dialog.component";
import { segsRouteConfig } from "src/app/shared/constants/segsRouteConfig.constants";
import { GridViewService } from "src/app/services/grid-view.service";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  standalone: false,
    selector: "app-location-list",
    templateUrl: "./location-list.component.html",
    styleUrls: [
        "./location-list.component.scss",
        "././../../shared/grid/core-grid/core-grid.component.scss",
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [detailExpandAnimation],
})
export class LocationListComponent
    extends BaseGridComponent<LocationGroup>
    implements OnInit, AfterViewInit, OnDestroy
{
    readonly permissionObject = RolePermissionObjects.LocationGroup;

    private destroy$ = new Subject<void>();
    expandedElements: Set<LocationGroup> = new Set();
    locationGroups: LocationGroup[] = [];
    filteredGroups: LocationGroup[] = [];
    tabs = segsRouteConfig;

    constructor(
        locationGroupService: LocationGroupService,
        private locationService: LocationService,
        toastService: ToastService,
        dialog: MatDialog,
        commonService: CommonService,
        protected gridViewService: GridViewService,
    ) {
        super(toastService, dialog, commonService, gridViewService);
        this.gridService = locationGroupService;
        this.displayedColumns = [nameOf<LocationGroup>((x) => x.name)];
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.dataSource
            .connect(null)
            .pipe(takeUntil(this.destroy$))
            .subscribe((groups) => {
                this.filteredGroups = groups;
                groups.forEach((group) => this.expandedElements.add(group));
            });
    }

    ngAfterViewInit(): void {
        this.loadLocationGroups();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadLocationGroups(): void {
        this.locationService
            .get("", "", "", 0, 100, "")
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (locations) => {
                    const groups = this.dataSource.getData();
                    if (groups.length > 0) {
                        this.locationGroups = [...groups]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((group) => ({
                                ...group,
                                locations: locations.data
                                    .filter(
                                        (location) =>
                                            location.locationGroupId ===
                                            group.id,
                                    )
                                    .sort((a, b) =>
                                        a.name.localeCompare(b.name),
                                    ),
                            }));
                        this.filteredGroups = this.locationGroups;
                    }
                },
                error: () => {
                    this.toastService.showError("Error loading locations");
                },
            });
    }

    expandLocationGroup(locationGroupId: number | string): LocationModel[] {
        return this.filteredGroups.find((group) => group.id === locationGroupId)
            ?.locations;
    }

    toggleRow(group: LocationGroup): void {
        if (this.expandedElements.has(group)) {
            this.expandedElements.delete(group);
        } else {
            this.expandedElements.add(group);
        }
    }

    isExpanded(group: LocationGroup): boolean {
        return this.expandedElements.has(group);
    }

    openLocationGroupDialog(locationGroupId: string = ''): void {
        const locationGroupModel: IFormDialogData = {
            id: locationGroupId,
        };

        this.openDialog(LocationGroupDialog, locationGroupModel);
    }

    applyFilter(filterValue: string): void {
        const lowerCaseFilter = filterValue.toLowerCase();

        if (!lowerCaseFilter) {
            this.filteredGroups = this.locationGroups;
        } else {
            this.filteredGroups = this.locationGroups
                .map((group) => {
                    const filteredLocations =
                        group.locations?.filter((location) =>
                            location.name
                                .toLowerCase()
                                .includes(lowerCaseFilter),
                        ) || [];
                    const groupMatches = group.name
                        .toLowerCase()
                        .includes(lowerCaseFilter);
                    if (groupMatches || filteredLocations.length > 0) {
                        return {
                            ...group,
                            locations: filteredLocations,
                        };
                    }
                    return null;
                })
                .filter((group) => group !== null) as LocationGroup[];
        }

        this.dataSource.updateData(this.filteredGroups);
    }

    openLocationDialog(
        locationId: string = '',
        locationGroupId: string = '',
    ): void {
        const locationModel: ILocationDialogData = {
            id: locationId,
            groupId: locationGroupId,
        };

        this.openDialog(LocationDialogComponent, locationModel);
    }

    handleDialogClose(
        dialogRef: any,
        reloadAction?: (result?: unknown) => void,
        successAction?: () => any,
        cancelAction?: () => any,
    ): void {
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: unknown) => {
                if (this.isSavedDialogResult(result)) {
                    const dialogType =
                        dialogRef._containerInstance._config.ariaLabel ||
                        dialogRef.componentInstance?.constructor.name;

                    switch (dialogType) {
                        case "LocationDialogComponent":
                        case "Location":
                            reloadAction
                                ? reloadAction(result)
                                : this.loadLocationGroups();
                            break;
                        case "LocationGroupDialog":
                            reloadAction ? reloadAction(result) : this.loadPage();
                            break;
                    }

                    if (successAction) {
                        successAction();
                    }
                } else {
                    if (cancelAction) {
                        cancelAction();
                    }
                }
            });
    }

    openLocationDeleteDialog(location: LocationModel): void {
        this.openDeleteDialog(
            DeleteDialog,
            location,
            this.locationService,
            "Location",
        );
    }
}
